import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  fetchIndustries,
  fetchRoleData,
  setSelectedIndustry,
  setSelectedRole,
  toggleQuestionAsked,
  addQuestionNote,
  togglePitchChecked,
  addPitchNote,
  toggleAdvancedChecked,
  addAdvancedNote
} from '../store/slices/salesPlaybookSlice';
import { recallWebSocketService } from '../services/recallWebSocketService';
import { sessionManager } from '../services/sessionManager';

interface SalesPlaybookPanelProps {
  height?: number;
  onHeightChange?: (height: number) => void;
  minHeight?: number;
  maxHeight?: number;
  isSessionActive?: boolean;
}

const SalesPlaybookPanel: React.FC<SalesPlaybookPanelProps> = ({
  height = 320,
  onHeightChange,
  minHeight = 150,
  maxHeight = 800,
  isSessionActive = false
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    industries,
    industriesLoading,
    selectedIndustry,
    selectedIndustryDisplayName,
    selectedRole,
    selectedRoleDisplayName,
    availableRoles,
    currentPitch,
    currentAdvanced,
    questions,
    dataLoading,
    askedQuestions,
    questionNotes,
    checkedPitches,
    pitchNotes,
    checkedAdvanced,
    advancedNotes,
    error
  } = useSelector((state: RootState) => state.salesPlaybook);

  const [isPitchCollapsed, setIsPitchCollapsed] = useState(true);
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = useState(true);
  const [isQuestionsCollapsed, setIsQuestionsCollapsed] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [editingPitchNoteId, setEditingPitchNoteId] = useState<string | null>(null);
  const [pitchNoteText, setPitchNoteText] = useState('');
  const [editingAdvancedNoteId, setEditingAdvancedNoteId] = useState<string | null>(null);
  const [advancedNoteText, setAdvancedNoteText] = useState('');

  // Debug: Log when askedQuestions changes
  useEffect(() => {
    console.log('[SalesPlaybookPanel] askedQuestions updated:', askedQuestions, 'for role:', selectedRole);
  }, [askedQuestions, selectedRole]);

  // Fetch industries on mount
  useEffect(() => {
    dispatch(fetchIndustries());
  }, [dispatch]);

  // Fetch role data when industry or role changes
  useEffect(() => {
    if (selectedIndustry && selectedRole) {
      console.log('[SalesPlaybookPanel] Fetching role data for:', selectedIndustry, selectedRole);
      dispatch(fetchRoleData({ industry: selectedIndustry, roleName: selectedRole }));
    }
  }, [dispatch, selectedIndustry, selectedRole]);

  // Auto-save pre-selection when session becomes active
  useEffect(() => {
    if (isSessionActive && selectedIndustry && selectedRole) {
      const roomId = sessionManager.getRoomId();
      if (roomId && recallWebSocketService.isConnected()) {
        recallWebSocketService.sendTalkTrackUpdate({
          roomId,
          updateType: 'selection',
          industry: selectedIndustry,
          industryDisplayName: selectedIndustryDisplayName || selectedIndustry,
          role: selectedRole,
          roleDisplayName: selectedRoleDisplayName || selectedRole,
          totalQuestionsAvailable: questions.length
        });
      }
    }
  }, [isSessionActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const industryKey = e.target.value;
    if (!industryKey) {
      dispatch(setSelectedIndustry(null));
      return;
    }

    const industry = industries.find(i => i.industry === industryKey);
    if (industry) {
      dispatch(setSelectedIndustry(industry));

      // Send to saver via WebSocket only if session is active
      const roomId = sessionManager.getRoomId();
      if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
        recallWebSocketService.sendTalkTrackUpdate({
          roomId,
          updateType: 'selection',
          industry: industryKey,
          industryDisplayName: industry.displayName,
          role: 'general',
          roleDisplayName: 'General',
          totalQuestionsAvailable: 0
        });
      }
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleName = e.target.value;
    const role = availableRoles.find(r => r.name === roleName);
    if (role) {
      dispatch(setSelectedRole(role));

      // Send to saver via WebSocket only if session is active
      const roomId = sessionManager.getRoomId();
      if (isSessionActive && roomId && selectedIndustry && recallWebSocketService.isConnected()) {
        recallWebSocketService.sendTalkTrackUpdate({
          roomId,
          updateType: 'selection',
          industry: selectedIndustry,
          industryDisplayName: selectedIndustryDisplayName || '',
          role: role.name,
          roleDisplayName: role.displayName,
          totalQuestionsAvailable: questions.length
        });
      }
    }
  };

  const handleQuestionToggle = (questionId: string) => {
    const question = questions.find(q => q._id === questionId);
    const isCurrentlyAsked = askedQuestions.includes(questionId);

    dispatch(toggleQuestionAsked(questionId));

    // Auto-open note input when checking (not unchecking)
    if (!isCurrentlyAsked) {
      setEditingNoteId(questionId);
      setNoteText('');
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendTalkTrackUpdate({
        roomId,
        updateType: 'question_asked',
        questionId,
        questionText: question?.questionText || '',
        tags: question?.tags || [],
        isAsked: !isCurrentlyAsked,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }
  };

  const handleSaveNote = (questionId: string) => {
    const question = questions.find(q => q._id === questionId);
    const trimmedNote = noteText.trim();

    if (trimmedNote) {
      dispatch(addQuestionNote({ questionId, note: trimmedNote }));
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendTalkTrackUpdate({
        roomId,
        updateType: 'question_note',
        questionId,
        questionText: question?.questionText || '',
        note: trimmedNote,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }

    setEditingNoteId(null);
    setNoteText('');
  };

  // Pitch handlers
  const handlePitchToggle = (pitchId: string, pitchText: string) => {
    const isCurrentlyChecked = checkedPitches.includes(pitchId);
    dispatch(togglePitchChecked(pitchId));

    // Auto-open note input when checking (not unchecking)
    if (!isCurrentlyChecked) {
      setEditingPitchNoteId(pitchId);
      setPitchNoteText('');
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendSalesPlaybookUpdate({
        roomId,
        updateType: 'pitch_checked',
        pitchId,
        pitchText,
        isChecked: !isCurrentlyChecked,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }
  };

  const handleSavePitchNote = (pitchId: string, pitchText: string) => {
    const trimmedNote = pitchNoteText.trim();

    if (trimmedNote) {
      dispatch(addPitchNote({ pitchId, note: trimmedNote }));
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendSalesPlaybookUpdate({
        roomId,
        updateType: 'pitch_note',
        pitchId,
        pitchText,
        note: trimmedNote,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }

    setEditingPitchNoteId(null);
    setPitchNoteText('');
  };

  // Advanced handlers
  const handleAdvancedToggle = (advancedId: string, advancedText: string) => {
    const isCurrentlyChecked = checkedAdvanced.includes(advancedId);
    dispatch(toggleAdvancedChecked(advancedId));

    // Auto-open note input when checking (not unchecking)
    if (!isCurrentlyChecked) {
      setEditingAdvancedNoteId(advancedId);
      setAdvancedNoteText('');
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendSalesPlaybookUpdate({
        roomId,
        updateType: 'advanced_checked',
        advancedId,
        advancedText,
        isChecked: !isCurrentlyChecked,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }
  };

  const handleSaveAdvancedNote = (advancedId: string, advancedText: string) => {
    const trimmedNote = advancedNoteText.trim();

    if (trimmedNote) {
      dispatch(addAdvancedNote({ advancedId, note: trimmedNote }));
    }

    // Send to saver via WebSocket only if session is active
    const roomId = sessionManager.getRoomId();
    if (isSessionActive && roomId && recallWebSocketService.isConnected()) {
      recallWebSocketService.sendSalesPlaybookUpdate({
        roomId,
        updateType: 'advanced_note',
        advancedId,
        advancedText,
        note: trimmedNote,
        industry: selectedIndustry || undefined,
        industryDisplayName: selectedIndustryDisplayName || undefined,
        role: selectedRole || undefined,
        roleDisplayName: selectedRoleDisplayName || undefined
      });
    }

    setEditingAdvancedNoteId(null);
    setAdvancedNoteText('');
  };

  // Resize handling
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.min(maxHeight, Math.max(minHeight, startHeight + deltaY));
      onHeightChange?.(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight, maxHeight, onHeightChange]);

  return (
    <div className="flex flex-col bg-white rounded-lg shadow border border-gray-200" style={{ height }}>
      {/* Header with dropdowns */}
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Sales Playbook</h3>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedIndustry || ''}
            onChange={handleIndustryChange}
            disabled={industriesLoading}
            className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Industry...</option>
            {industries.map(ind => (
              <option key={ind.industry} value={ind.industry}>
                {ind.displayName}
              </option>
            ))}
          </select>
          <select
            value={selectedRole}
            onChange={handleRoleChange}
            disabled={!selectedIndustry || availableRoles.length === 0}
            className="w-32 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {availableRoles.map(role => (
              <option key={role.name} value={role.name}>
                {role.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
        {error && (
          <div className="text-red-600 text-sm mb-2">{error}</div>
        )}

        {!selectedIndustry ? (
          <div className="text-gray-400 text-sm text-center py-4">
            Select an industry to view sales playbook
          </div>
        ) : dataLoading ? (
          <div className="text-gray-400 text-sm text-center py-4">
            Loading...
          </div>
        ) : (
          <>
            {/* Intro Pitch Section */}
            {currentPitch.length > 0 && (
              <div className="mb-3">
                <button
                  onClick={() => setIsPitchCollapsed(!isPitchCollapsed)}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 hover:text-gray-800"
                >
                  <span className="transform transition-transform" style={{ transform: isPitchCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                    ▼
                  </span>
                  Intro Pitch
                </button>
                {!isPitchCollapsed && (
                  <ul className="space-y-2 pl-3">
                    {currentPitch.map((bullet, idx) => {
                      const pitchId = `pitch-${idx}`;
                      const isChecked = checkedPitches.includes(pitchId);
                      const note = pitchNotes[pitchId];
                      const isEditingNote = editingPitchNoteId === pitchId;

                      return (
                        <li key={idx} className="text-sm">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handlePitchToggle(pitchId, bullet)}
                              disabled={!isSessionActive}
                              className={`mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={!isSessionActive ? 'Start a session to mark pitches' : ''}
                            />
                            <div className="flex-1">
                              <span className={`${isChecked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                {bullet}
                              </span>

                              {/* Note display/edit */}
                              {isSessionActive && isEditingNote ? (
                                <div className="mt-1 flex gap-1">
                                  <input
                                    type="text"
                                    value={pitchNoteText}
                                    onChange={e => setPitchNoteText(e.target.value)}
                                    placeholder="Add note..."
                                    className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                                    autoFocus
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        handleSavePitchNote(pitchId, bullet);
                                      } else if (e.key === 'Escape') {
                                        setEditingPitchNoteId(null);
                                        setPitchNoteText('');
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => handleSavePitchNote(pitchId, bullet)}
                                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : note ? (
                                <div
                                  onClick={() => {
                                    if (isSessionActive) {
                                      setEditingPitchNoteId(pitchId);
                                      setPitchNoteText(note.note);
                                    }
                                  }}
                                  className={`mt-1 text-xs text-gray-500 italic ${isSessionActive ? 'cursor-pointer hover:text-gray-700' : ''}`}
                                >
                                  Note: {note.note}
                                </div>
                              ) : isSessionActive ? (
                                <button
                                  onClick={() => {
                                    // Auto-check when adding note
                                    if (!isChecked) {
                                      handlePitchToggle(pitchId, bullet);
                                    } else {
                                      setEditingPitchNoteId(pitchId);
                                      setPitchNoteText('');
                                    }
                                  }}
                                  className="mt-1 text-xs text-blue-500 hover:text-blue-700"
                                >
                                  + Add note
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Qualifying Questions - flat ordered list */}
            {questions.length > 0 && (
              <div className="mb-3">
                <button
                  onClick={() => setIsQuestionsCollapsed(!isQuestionsCollapsed)}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 hover:text-gray-800"
                >
                  <span className="transform transition-transform" style={{ transform: isQuestionsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                    ▼
                  </span>
                  Qualifying Questions
                </button>
                {!isQuestionsCollapsed && (
                  <ul className="space-y-2 pl-3">
                    {questions.map(q => {
                      const isAsked = askedQuestions.includes(q._id);
                      const note = questionNotes[q._id];
                      const isEditingNote = editingNoteId === q._id;

                      return (
                        <li key={q._id} className="text-sm">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isAsked}
                              onChange={() => handleQuestionToggle(q._id)}
                              disabled={!isSessionActive}
                              className={`mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={!isSessionActive ? 'Start a session to mark questions' : ''}
                            />
                            <div className="flex-1">
                              <span className={`${isAsked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                {q.questionText}
                              </span>

                              {isSessionActive && isEditingNote ? (
                                <div className="mt-1 flex gap-1">
                                  <input
                                    type="text"
                                    value={noteText}
                                    onChange={e => setNoteText(e.target.value)}
                                    placeholder="Add note..."
                                    className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                                    autoFocus
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        handleSaveNote(q._id);
                                      } else if (e.key === 'Escape') {
                                        setEditingNoteId(null);
                                        setNoteText('');
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => handleSaveNote(q._id)}
                                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : note ? (
                                <div
                                  onClick={() => {
                                    if (isSessionActive) {
                                      setEditingNoteId(q._id);
                                      setNoteText(note.note);
                                    }
                                  }}
                                  className={`mt-1 text-xs text-gray-500 italic ${isSessionActive ? 'cursor-pointer hover:text-gray-700' : ''}`}
                                >
                                  Note: {note.note}
                                </div>
                              ) : isSessionActive ? (
                                <button
                                  onClick={() => {
                                    if (!isAsked) {
                                      handleQuestionToggle(q._id);
                                    } else {
                                      setEditingNoteId(q._id);
                                      setNoteText('');
                                    }
                                  }}
                                  className="mt-1 text-xs text-blue-500 hover:text-blue-700"
                                >
                                  + Add note
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Advanced Section */}
            {currentAdvanced && currentAdvanced.length > 0 && (
              <div className="mb-3 mt-3">
                <button
                  onClick={() => setIsAdvancedCollapsed(!isAdvancedCollapsed)}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 hover:text-gray-800"
                >
                  <span className="transform transition-transform" style={{ transform: isAdvancedCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                    ▼
                  </span>
                  Deal Progression
                </button>
                {!isAdvancedCollapsed && (
                  <ul className="space-y-2 pl-3">
                    {currentAdvanced.map((bullet, idx) => {
                      const advancedId = `advanced-${idx}`;
                      const isChecked = checkedAdvanced.includes(advancedId);
                      const note = advancedNotes[advancedId];
                      const isEditingNote = editingAdvancedNoteId === advancedId;

                      return (
                        <li key={idx} className="text-sm">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleAdvancedToggle(advancedId, bullet)}
                              disabled={!isSessionActive}
                              className={`mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${!isSessionActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={!isSessionActive ? 'Start a session to mark items' : ''}
                            />
                            <div className="flex-1">
                              <span className={`${isChecked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                {bullet}
                              </span>

                              {/* Note display/edit */}
                              {isSessionActive && isEditingNote ? (
                                <div className="mt-1 flex gap-1">
                                  <input
                                    type="text"
                                    value={advancedNoteText}
                                    onChange={e => setAdvancedNoteText(e.target.value)}
                                    placeholder="Add note..."
                                    className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                                    autoFocus
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        handleSaveAdvancedNote(advancedId, bullet);
                                      } else if (e.key === 'Escape') {
                                        setEditingAdvancedNoteId(null);
                                        setAdvancedNoteText('');
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => handleSaveAdvancedNote(advancedId, bullet)}
                                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : note ? (
                                <div
                                  onClick={() => {
                                    if (isSessionActive) {
                                      setEditingAdvancedNoteId(advancedId);
                                      setAdvancedNoteText(note.note);
                                    }
                                  }}
                                  className={`mt-1 text-xs text-gray-500 italic ${isSessionActive ? 'cursor-pointer hover:text-gray-700' : ''}`}
                                >
                                  Note: {note.note}
                                </div>
                              ) : isSessionActive ? (
                                <button
                                  onClick={() => {
                                    // Auto-check when adding note
                                    if (!isChecked) {
                                      handleAdvancedToggle(advancedId, bullet);
                                    } else {
                                      setEditingAdvancedNoteId(advancedId);
                                      setAdvancedNoteText('');
                                    }
                                  }}
                                  className="mt-1 text-xs text-blue-500 hover:text-blue-700"
                                >
                                  + Add note
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {currentPitch.length === 0 && questions.length === 0 && !dataLoading && (
              <div className="text-gray-400 text-sm text-center py-4">
                No content for this role yet
              </div>
            )}
          </>
        )}
      </div>

      {/* Resize handle */}
      {onHeightChange && (
        <div
          className="h-2 bg-gray-100 cursor-ns-resize flex items-center justify-center hover:bg-gray-200 border-t border-gray-200"
          onMouseDown={handleResizeStart}
        >
          <div className="w-8 h-1 bg-gray-400 rounded" />
        </div>
      )}
    </div>
  );
};

// Backward compatibility alias
export const TalkTracksPanel = SalesPlaybookPanel;

export default SalesPlaybookPanel;
